import { Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const normalized = (value) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/**
 * The addresses of the record, to tick in a line of addresses (RecipientField):
 * a search, then a box per address. An address already in another line
 * (`elsewhere`: { address: « En Cc » }) cannot be ticked here.
 * `options`: [{ name, email, tag }]; `recipients`: [{ name, address }].
 */
const RecipientPicker = ({ label, options, recipients, elsewhere, onChange, texts }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => ref.current && !ref.current.contains(event.target) && setOpen(false);
    const escape = (event) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const taken = new Set(recipients.map((one) => one.address.toLowerCase()));
  const words = normalized(search).trim();
  const shown = options.filter((option) => !words || normalized(option.name).includes(words) || normalized(option.email).includes(words));
  const toggle = (option) => {
    const address = option.email.toLowerCase();
    onChange(
      taken.has(address)
        ? recipients.filter((one) => one.address.toLowerCase() !== address)
        : [...recipients, { name: option.name ?? '', address: option.email }],
    );
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-label={texts.open(label)}
        aria-expanded={open}
        title={texts.open(label)}
        onClick={() => {
          setOpen((current) => !current);
          setSearch('');
        }}
        className={`flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-gray-100 hover:text-gray-900 ${
          open ? 'bg-gray-100 text-gray-900' : 'text-gray-500'
        }`}
      >
        <Users size={16} strokeWidth={2} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-80 rounded-lg border border-gray-100 bg-white shadow-lg motion-safe:animate-menuIn">
          <div className="border-b border-gray-100 p-2">
            <input
              type="search"
              aria-label={texts.search}
              placeholder={texts.search}
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-9 w-full rounded-[0.4rem] border border-gray-200 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-yellow-500 focus:outline-none focus:ring-0"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto p-1">
            {!shown.length && <li className="px-3 py-2 text-sm text-gray-500">{texts.empty}</li>}
            {shown.map((option) => {
              const address = option.email.toLowerCase();
              const where = elsewhere[address];
              return (
                <li key={address}>
                  <label
                    className={`flex items-center gap-3 rounded-md px-3 py-2 ${
                      where ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="checkbox-primary shrink-0"
                      checked={taken.has(address)}
                      disabled={Boolean(where)}
                      onChange={() => toggle(option)}
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="flex items-center gap-2 text-sm text-gray-900">
                        <span className="truncate">{option.name || option.email}</span>
                        {option.tag && <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{option.tag}</span>}
                      </span>
                      {option.name && <span className="truncate text-xs text-gray-500">{option.email}</span>}
                    </span>
                    {where && <span className="shrink-0 text-xs text-gray-500">{where}</span>}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RecipientPicker;
